import { ChevronRight, RotateCw } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { SettingsStatusTag } from './SettingsStatusTag';
import type { DirectoryRowConfig } from './settingsDirectoryConfig';
import type { DirectoryRowState } from './useSettingsDirectory';

interface SettingsDirectoryRowProps {
  row: DirectoryRowConfig;
  state: DirectoryRowState;
}

/**
 * One settings destination. The label is a stretched link so the whole row
 * is the hit target, while the inline Retry button stays independently
 * clickable (a button may not live inside an anchor).
 */
export const SettingsDirectoryRow: React.FC<SettingsDirectoryRowProps> = ({
  row,
  state,
}) => {
  const renderSummary = () => {
    if (state.needsOutlet) {
      return (
        <span className="text-gray-400">
          Choose an outlet above to see this
        </span>
      );
    }
    if (state.isLoading) {
      return (
        <span
          aria-label="Loading"
          className="inline-block h-3 w-48 max-w-full rounded bg-gray-100 motion-safe:animate-pulse"
        />
      );
    }
    if (state.isError) {
      return (
        <span className="inline-flex items-center gap-2 text-gray-500">
          Couldn’t load this summary
          <button
            type="button"
            onClick={state.retry}
            className="relative z-10 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-pos-primary hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pos-primary"
          >
            <RotateCw size={12} aria-hidden />
            Retry
          </button>
        </span>
      );
    }
    return <span className="text-gray-500">{state.summary?.text}</span>;
  };

  return (
    <li className="group relative flex min-h-[3.25rem] items-center gap-4 px-4 py-3 transition-colors duration-150 focus-within:bg-gray-50 hover:bg-gray-50">
      <div className="min-w-0 flex-1 md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:items-center md:gap-6">
        <Link
          to={row.to}
          className="block font-medium text-gray-900 outline-none after:absolute after:inset-0 after:rounded-[inherit] focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-pos-primary"
        >
          {row.label}
        </Link>
        <div className="mt-0.5 text-sm tabular-nums md:mt-0">
          {renderSummary()}
        </div>
      </div>
      {state.summary ? (
        <SettingsStatusTag status={state.summary.status} />
      ) : null}
      <ChevronRight
        size={16}
        aria-hidden
        className="shrink-0 text-gray-300 transition-transform duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-gray-500 motion-reduce:transition-none"
      />
    </li>
  );
};
